/**
 * Search Geocoding Module
 *
 * Handles geocoding for search near field when form is submitted.
 * Replicates v2 logic from class-geodir-frontend-scripts.php js_location_functions()
 */

let Sgeocoder = null;
let defaultNearText = '';
let defaultSearchText = '';

/**
 * Get current map provider
 * @returns {string} 'google', 'osm', or null
 */
function getMapProvider() {
	// Check window.gdMaps first (set by map templates)
	if (window.gdMaps) {
		return window.gdMaps;
	}

	// Detect based on loaded libraries
	if (window.google?.maps) {
		return 'google';
	}

	if (window.L && typeof window.L.version !== 'undefined') {
		return 'osm';
	}

	return null;
}

/**
 * Clear search coordinates when near field changes
 */
function clearSearchCoordinates() {
	document.querySelectorAll('.sgeo_lat').forEach(el => el.value = '');
	document.querySelectorAll('.sgeo_lon').forEach(el => el.value = '');
}

/**
 * Clear near field and coordinates (called from template onclick)
 */
function clearNearField() {
	document.querySelectorAll('.snear').forEach(el => {
		el.value = '';
		el.dispatchEvent(new Event('change', {bubbles: true}));
	});
	clearSearchCoordinates();
}

/**
 * Clear search text field (called from template onclick)
 */
function clearSearchField() {
	document.querySelectorAll('.search_text').forEach(el => {
		el.value = '';
		el.dispatchEvent(new Event('change', {bubbles: true}));
		el.dispatchEvent(new Event('keyup', {bubbles: true}));
	});
}

/**
 * Initialize search geocoding
 */
export function initSearchGeocoding() {
	const mapProvider = getMapProvider();

	// Initialize Google geocoder if available
	if (mapProvider === 'google' && window.google?.maps?.Geocoder) {
		Sgeocoder = new window.google.maps.Geocoder();
	}

	// Get default texts from params
	defaultNearText = window.geodir_params?.default_near_text || '';
	defaultSearchText = window.geodir_params?.default_search_text || '';

	// Expose clear functions globally for template onclick handlers
	window.geodirClearNearField = clearNearField;
	window.geodirClearSearchField = clearSearchField;

	// Setup submit handlers
	geodirSetupSubmitSearch();

	// Re-setup after AJAX form reload
	document.body.addEventListener('geodir:searchFormLoaded', () => {
		geodirSetupSubmitSearch();
	});
}

/**
 * Setup submit search handler
 * v2 equivalent: geodir_setup_submit_search()
 */
function geodirSetupSubmitSearch() {
	const buttons = document.querySelectorAll('.geodir_submit_search');

	buttons.forEach(button => {
		// Remove existing listeners by cloning
		const newButton = button.cloneNode(true);
		button.parentNode.replaceChild(newButton, button);
	});

	// Re-query after clone and add new listeners
	document.querySelectorAll('.geodir_submit_search').forEach(button => {
		button.addEventListener('click', handleSubmitClick);
	});

	// Clear GPS on near field keyup (if no location type)
	if (!document.querySelector('input.geodir-location-search-type') && document.querySelector('[name="snear"]')) {
		document.querySelectorAll('[name="snear"]').forEach(nearInput => {
			nearInput.addEventListener('keyup', clearSearchCoordinates);
			nearInput.addEventListener('change', clearSearchCoordinates);
		});
	}
}

/**
 * Handle submit button click
 * v2 equivalent: geodir_setup_submit_search() click handler (line 181)
 */
function handleSubmitClick(e) {
	e.preventDefault();

	const form = e.target.closest('form');
	const s = ' ';

	// Clear default search text
	const searchText = form.querySelector('.search_text');
	if (searchText && (searchText.value === '' || searchText.value === defaultSearchText)) {
		searchText.value = s;
	}

	// Check if need geocoding
	const dist = getDistance(form);
	const sortBy = form.querySelector('select[name="sort_by"]')?.value;
	const snear = form.querySelector('.snear');
	const nearValue = snear?.value || '';
	const hasLocationType = form.querySelector('.geodir-location-search-type')?.value;

	const needsGeocoding = (
		dist > 0 ||
		sortBy === 'nearest' ||
		sortBy === 'farthest' ||
		(nearValue !== '' && nearValue !== defaultNearText && !hasLocationType)
	);

	if (needsGeocoding) {
		// OSM: Format UK postcodes (add space before last 3 chars)
		const mapProvider = getMapProvider();
		if (mapProvider === 'osm' && nearValue) {
			const ukPostcodeRegex = /^([A-Za-z][A-Ha-hJ-Yj-y]?[0-9][A-Za-z0-9]?[0-9][A-Za-z]{2}|[Gg][Ii][Rr] ?0[Aa]{2})$/;
			if (ukPostcodeRegex.test(nearValue)) {
				const formatted = nearValue.replace(/.{3}$/, ' $&');
				snear.value = formatted;
			}
		}

		geodirSetSearch(form);
	} else {
		// No geocoding needed
		if (snear) snear.value = '';
		form.submit();
	}
}

/**
 * Get distance value
 */
function getDistance(form) {
	const distRadio = form.querySelector('#sdistance input[type="radio"]:checked');
	return distRadio ? parseInt(distRadio.value) : 0;
}

/**
 * Set search - calls geocoding
 * v2 equivalent: geodir_setsearch() (line 270)
 */
function geodirSetSearch(form) {
	const dist = getDistance(form);
	const sortBy = form.querySelector('select[name="sort_by"]')?.value;
	const snear = form.querySelector('.snear');
	const nearValue = snear?.value || '';

	// Set default location if needed
	if ((dist > 0 || sortBy === 'nearest' || sortBy === 'farthest') &&
	    (nearValue === '' || nearValue === defaultNearText)) {
		const defaultLocation = window.geodir_params?.default_location || '';
		if (snear) snear.value = defaultLocation;
	}

	geocodeAddress(form);
}

/**
 * Geocode address
 * v2 equivalent: geocodeAddress() (line 286)
 */
function geocodeAddress(form) {
	const mapProvider = getMapProvider();

	// Reinitialize geocoder for Google
	if (mapProvider === 'google' && window.google?.maps?.Geocoder) {
		Sgeocoder = new window.google.maps.Geocoder();
	}

	const snear = form.querySelector('.snear');
	const sgeoLat = form.querySelector('.sgeo_lat');
	const sgeoLon = form.querySelector('.sgeo_lon');
	const nearValue = snear?.value || '';

	// Skip if empty or already has coordinates
	if (!nearValue || (sgeoLat?.value && sgeoLon?.value)) {
		form.submit();
		return;
	}

	const address = nearValue;

	// Skip if default text
	if (address.trim() === defaultNearText) {
		form.submit();
		return;
	}

	// Geocode based on map type
	if (mapProvider === 'google') {
		const geocodeParams = { address };

		// Add zip code component restriction if applicable
		if (geodirIsZipCode(address)) {
			geocodeParams.componentRestrictions = { postalCode: address };
		}

		Sgeocoder.geocode(geocodeParams, (results, status) => {
			if (status === 'OK' && results?.[0]?.geometry?.location) {
				updateSearchPosition(results[0].geometry.location, form);
			} else {
				alert(`Search was not successful for the following reason: ${status}`);
			}
		});
	} else if (mapProvider === 'osm') {
		geocodePositionOSM(false, address, false, false, (geo) => {
			if (geo?.lat && geo?.lon) {
				updateSearchPosition(geo, form);
			} else {
				alert('Search was not successful for the requested address.');
			}
		});
	} else {
		form.submit();
	}
}

/**
 * Update search position and submit form
 * v2 equivalent: updateSearchPosition() (line 275)
 */
function updateSearchPosition(latLng, form) {
	const mapProvider = getMapProvider();
	const lat = mapProvider === 'google' ? latLng.lat() : latLng.lat;
	const lng = mapProvider === 'google' ? latLng.lng() : (latLng.lon || latLng.lng);

	form.querySelectorAll('.sgeo_lat').forEach(el => el.value = lat);
	form.querySelectorAll('.sgeo_lon').forEach(el => el.value = lng);

	form.submit();
}

/**
 * Check if string is a zip code
 * v2 equivalent: geodirIsZipCode() (line 400)
 */
function geodirIsZipCode(string) {
	if (/^\d+$/.test(string)) {
		if (string.length > 3 && string.length < 7) {
			return true;
		}
	}
	return false;
}

/**
 * Geocode using OSM Nominatim
 */
function geocodePositionOSM(reverse, address, countryCodes, options, callback) {
	const params = new URLSearchParams({
		q: address,
		format: 'json',
		addressdetails: '1',
		limit: '1',
		'accept-language': 'en'
	});

	if (countryCodes) {
		params.append('countrycodes', countryCodes);
	}

	fetch(`https://nominatim.openstreetmap.org/search?${params}`, {
		headers: {
			'User-Agent': 'GeoDirectory'
		}
	})
		.then(res => res.json())
		.then(results => {
			if (results && results.length > 0) {
				callback({
					lat: parseFloat(results[0].lat),
					lon: parseFloat(results[0].lon)
				});
			} else {
				callback(undefined);
			}
		})
		.catch(() => callback(undefined));
}
